import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const ChecklistRow = ({ done, title, description, actions }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={2}
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    sx={{ py: 1.5 }}
  >
    <Box sx={{ color: done ? 'success.main' : 'text.secondary', display: 'flex', pt: 0.25 }}>
      {done ? (
        <CheckCircleOutlineIcon fontSize="small" />
      ) : (
        <RadioButtonUncheckedIcon fontSize="small" />
      )}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
    {actions && (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {actions}
      </Stack>
    )}
  </Stack>
);

const GettingStarted = ({
  games = [],
  canCreate = false,
  canCreateElsewhere = false,
  canInvite = false,
  selectedSpaceId,
  showChecklist = false,
  onCreate,
  onJoin,
  onDismiss,
}) => {
  const navigate = useNavigate();
  const isEmpty = games.length === 0;
  const hasGame = games.length > 0;
  const hasPublished = games.some((game) => game.is_published);

  const openInvite = () => {
    if (!selectedSpaceId) return;
    navigate(`/gamespaces/${selectedSpaceId}/manage-users`);
  };

  if (!isEmpty && !showChecklist) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, mb: 4 }}>
      {isEmpty && (
        <Stack spacing={2} sx={{ mb: showChecklist ? 3 : 0 }}>
          <Typography variant="h5" component="h2">
            {canCreate ? 'This learnspace is empty' : 'No games in this learnspace yet'}
          </Typography>
          <Typography color="text.secondary">
            {canCreate
              ? 'Create a game from your learning content, then host it or play it yourself.'
              : canCreateElsewhere
                ? 'Games appear here when they are published. If someone is hosting, join with the session code. To create games, switch to a learnspace where you are a creator.'
                : 'Games appear here when they are published. If someone is hosting, join with the session code.'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {canCreate && (
              <Button variant="contained" onClick={onCreate}>
                Create game
              </Button>
            )}
            <Button
              variant={canCreate ? 'outlined' : 'contained'}
              color="primary"
              onClick={onJoin}
            >
              Enter session code
            </Button>
          </Stack>
        </Stack>
      )}

      {isEmpty && showChecklist && <Divider sx={{ mb: 2 }} />}

      {showChecklist && (
        <Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1">Getting started</Typography>
            <Button size="small" onClick={() => onDismiss()}>
              Dismiss
            </Button>
          </Stack>
          <ChecklistRow
            done={hasGame}
            title="Create a game"
            description="Describe the learners, add learning content, then generate."
            actions={
              !hasGame && !isEmpty ? (
                <Button size="small" variant="contained" onClick={onCreate}>
                  Create
                </Button>
              ) : null
            }
          />
          <ChecklistRow
            done={hasPublished}
            title="Review and publish"
            description={
              hasGame
                ? 'Open a game from the list below and publish it before you host a session.'
                : 'Publish the game before you host a session.'
            }
          />
          <ChecklistRow
            done={hasPublished}
            title="Play, host, or invite"
            description={
              hasGame
                ? 'Open a game from the list below to play or host. Invite people to this learnspace when you are ready.'
                : 'Play it yourself, host with a session code, or invite people to this learnspace.'
            }
            actions={
              hasPublished && canInvite ? (
                <Button size="small" variant="outlined" onClick={openInvite}>
                  Invite
                </Button>
              ) : null
            }
          />
        </Box>
      )}
    </Paper>
  );
};

export default GettingStarted;
